import React, { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { Button } from '../../components/ui/Button';
import { Input, Select } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { PageSpinner } from '../../components/ui/Spinner';

interface Department {
  id: string;
  name: string;
  code: string;
}

interface ServiceCategory {
  id: string;
  name: string;
  prefix: string;
  departmentId: string;
  departmentName?: string;
  avgServiceTimeMins: number;
  isActive: boolean;
  displayOrder: number;
}

interface ServiceForm {
  name: string;
  prefix: string;
  departmentId: string;
  avgServiceTimeMins: string;
  displayOrder: string;
}

const emptyForm = (deptId = ''): ServiceForm => ({
  name: '',
  prefix: '',
  departmentId: deptId,
  avgServiceTimeMins: '10',
  displayOrder: '1',
});

export function ServicesPage() {
  const { staff } = useAuthStore();
  const facilityId = staff!.facilityId;

  const [depts, setDepts] = useState<Department[]>([]);
  const [services, setServices] = useState<ServiceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<ServiceCategory | null>(null);
  const [form, setForm] = useState<ServiceForm>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [deptsData, svcData] = await Promise.all([
        apiGet<Department[]>(`/admin/facilities/${facilityId}/departments`),
        apiGet<ServiceCategory[]>(`/admin/facilities/${facilityId}/services`),
      ]);
      setDepts(deptsData);
      const deptMap = new Map(deptsData.map((d) => [d.id, d.name]));
      setServices(svcData.map((s) => ({ ...s, departmentName: deptMap.get(s.departmentId) })));
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm(depts[0]?.id ?? ''));
    setShowModal(true);
  };

  const openEdit = (s: ServiceCategory) => {
    setEditing(s);
    setForm({
      name: s.name,
      prefix: s.prefix,
      departmentId: s.departmentId,
      avgServiceTimeMins: String(s.avgServiceTimeMins),
      displayOrder: String(s.displayOrder),
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    const body = {
      ...form,
      prefix: form.prefix.toUpperCase(),
      avgServiceTimeMins: Number(form.avgServiceTimeMins),
      displayOrder: Number(form.displayOrder),
    };
    try {
      if (editing) {
        await apiPut(`/admin/facilities/${facilityId}/services/${editing.id}`, body);
      } else {
        await apiPost(`/admin/facilities/${facilityId}/services`, body);
      }
      setShowModal(false);
      await load();
    } catch (e) {
      setSaveError(String(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (s: ServiceCategory) => {
    await apiPut(`/admin/facilities/${facilityId}/services/${s.id}`, { isActive: !s.isActive });
    setServices((prev) => prev.map((x) => x.id === s.id ? { ...x, isActive: !x.isActive } : x));
  };

  const set = (k: keyof ServiceForm, v: string) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Service Categories</h1>
        <Button onClick={openCreate} disabled={depts.length === 0}>+ Add Service</Button>
      </div>

      {depts.length === 0 && !loading && (
        <p className="text-sm text-yellow-700 bg-yellow-50 rounded p-3 mb-4">
          Create at least one department first before adding services.
        </p>
      )}

      {loading && <PageSpinner />}
      {error && <p className="text-red-600">{error}</p>}

      {!loading && !error && (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Prefix', 'Name', 'Department', 'Avg Time', 'Active', ''].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {services.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No services yet.</td></tr>
              )}
              {services.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-sm font-bold text-blue-700">{s.prefix}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{s.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{s.departmentName}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{s.avgServiceTimeMins} min</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium ${s.isActive ? 'text-green-600' : 'text-gray-400'}`}>
                      {s.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 flex gap-2">
                    <Button size="sm" variant="secondary" onClick={() => openEdit(s)}>Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => toggleActive(s)}>
                      {s.isActive ? 'Disable' : 'Enable'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={showModal}
        title={editing ? 'Edit Service' : 'Add Service Category'}
        onClose={() => { setShowModal(false); setSaveError(null); }}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button form="svc-form" type="submit" loading={saving}>{editing ? 'Save' : 'Create'}</Button>
          </>
        }
      >
        <form id="svc-form" onSubmit={handleSubmit} className="space-y-4">
          <Select label="Department" value={form.departmentId} onChange={(e) => set('departmentId', e.target.value)} required>
            {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </Select>
          <Input label="Service Name" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. General Consultation" required />
          <Input
            label="Ticket Prefix (unique in facility)"
            value={form.prefix}
            onChange={(e) => set('prefix', e.target.value.toUpperCase().slice(0, 3))}
            placeholder="e.g. A, L, P"
            maxLength={3}
            required
            disabled={!!editing}
          />
          <Input label="Avg. Service Time (minutes)" type="number" value={form.avgServiceTimeMins} onChange={(e) => set('avgServiceTimeMins', e.target.value)} min={1} required />
          <Input label="Display Order" type="number" value={form.displayOrder} onChange={(e) => set('displayOrder', e.target.value)} />
          {saveError && <p className="text-sm text-red-600">{saveError}</p>}
        </form>
      </Modal>
    </div>
  );
}
